<template>
  <div class="">
    <login-form v-if="showLogin" />
    <div v-else class="flex flex-col gap-2.5 max-w-[440px] mx-auto pb-10">
      <button
        v-for="(button, index) in buttons"
        :key="button.key"
        type="button"
        class="w-full min-h-[60px] py-2.5 pl-3 pr-4 rounded-2xl flex items-center gap-3.5 text-left transition-transform active:scale-[0.98]"
        :style="variant(index).button"
        @click="button.action"
      >
        <div
          class="w-9 h-9 flex-none rounded-small flex items-center justify-center"
          :style="variant(index).tile"
        >
          <c-icon :name="button.icon" size="20px" />
        </div>
        <div
          class="flex-1 min-w-0 text-[15px]"
          :class="{ 'font-heading font-semibold': index === 0 }"
        >
          {{ button.label }}
        </div>
        <c-icon
          :name="button.external ? 'OpenInNewFilled' : 'ChevronRightRound'"
          size="20px"
          :style="variant(index).trail"
        />
      </button>
    </div>
  </div>
</template>

<script>
import LoginForm from "./LoginForm.vue";
import CIcon from "../../../core-ui/src/components/CIcon.vue";
import { mapState } from "pinia";
import { authStore } from "@/stores/auth";
import { mix, rgba } from "@/palette.js";
import api from "@/api.js";

export default {
  name: "CustomerHub",
  data() {
    return {
      defaultButtons: [],
      showLogin: false,
      showAppointments: false,
    };
  },
  async mounted() {
    if (this.organizationConfig.enableAppointments && this.currentLocation) {
      this.defaultButtons.push("appointments");
    }
    if (this.organizationConfig.enableReservations && this.currentLocation) {
      this.defaultButtons.push("reservations");
    }
    if (this.currentCustomer?.id) {
      this.defaultButtons.push("download");
      this.defaultButtons.push("profile");
    } else {
      this.defaultButtons.push("signup");
    }
    if (this.$route.query?.lastappUrl) {
      this.defaultButtons.push("login");
      this.defaultButtons.push("continueWithoutSignup");
    }
    if (this.currentCustomer?.id && this.$route.query.giftCardId) {
      let giftCardId = this.$route.query.giftCardId;
      try {
        await api.post(`/customer/${this.currentCustomer.id}/bindGiftCard`, {
          giftCardId: giftCardId,
        });
        this.$notificationGenerator.create({
          title: this.$t("gift-card-bound"),
          type: "success",
          content: this.$t("gift-card-bound-to-account"),
        });
      } catch (e) {
        this.$notificationGenerator.error(this.$t("gift-card-bind-error"));
      }
    }
    if (
      this.interestingLinksButtons.length === 0 &&
      this.defaultButtons.length === 1 &&
      this.defaultButtons.includes("signup")
    ) {
      this.handleGoToCreateCustomer();
    }
    if (
      this.interestingLinksButtons.length === 0 &&
      this.defaultButtons.length === 1 &&
      this.defaultButtons.includes("download")
    ) {
      this.handleGoToDownload();
    }
  },
  computed: {
    ...mapState(authStore, [
      "interestingLinks",
      "branding",
      "organizationConfig",
      "currentLocation",
      "currentCustomer",
      "palette",
    ]),
    shouldShowEarnPoints() {
      return this.$route.query?.lastappUrl && !this.$route.query.signupOrLogin;
    },
    interestingLinksButtons() {
      return this.interestingLinks
        .filter((il) => il.showInSignup && il.url)
        .map((link) => ({
          title: link.title,
          url: link.url,
        }));
    },
    defaultButtonsConfig() {
      return {
        appointments: {
          icon: "CalendarMonthRound",
          label: this.$t("make-appointment"),
          action: () =>
            this.$router.push({
              name: "appointmentPage",
              params: { ...this.$route.params },
            }),
        },
        reservations: {
          icon: "EventAvailableRound",
          label: this.$t("reservations.make-reservation"),
          action: () =>
            this.$router.push({
              name: "reservationPage",
              params: { ...this.$route.params },
            }),
        },
        download: {
          icon: "FileDownloadOutlined",
          label: this.$t("download-pass"),
          action: this.handleGoToDownload,
        },
        profile: {
          icon: "AccountCircleRound",
          label: this.$t("view-profile"),
          action: this.handleGoToProfile,
        },
        signup: {
          icon: "PersonAddRound",
          label: this.$t("create-new-account"),
          action: this.handleGoToCreateCustomer,
        },
        login: {
          icon: "PersonFilled",
          label: this.$t("have-account"),
          action: () => (this.showLogin = true),
        },
        continueWithoutSignup: {
          icon: "ArrowForwardRound",
          label: this.$t("dont-want-add-points"),
          action: this.handleContinueWithoutSignup,
        },
      };
    },
    // The first button is highlighted: booking when enabled, otherwise the
    // next action for the customer.
    buttons() {
      let defaults = this.defaultButtons.map((key) => ({
        key,
        ...this.defaultButtonsConfig[key],
      }));
      let links = this.interestingLinksButtons.map((link, index) => ({
        key: `link-${index}`,
        icon: "OpenInNewFilled",
        label: link.title,
        external: true,
        action: () => this.handleLinkClick(link.url),
      }));
      return [...defaults, ...links];
    },
    variants() {
      let p = this.palette;
      return {
        primary: {
          button: {
            backgroundColor: p.accent,
            color: p.onAccent,
            boxShadow: `0 8px 20px -6px ${rgba(p.accent, 0.55)}`,
          },
          tile: {
            backgroundColor: rgba(
              p.onAccent,
              p.onAccent === "#FFFFFF" ? 0.18 : 0.08
            ),
          },
          trail: {},
        },
        secondary: {
          button: {
            backgroundColor: p.surface,
            color: p.text,
            boxShadow:
              "0 1px 2px rgba(10, 19, 42, 0.06), 0 6px 16px rgba(10, 19, 42, 0.06)",
          },
          tile: {
            backgroundColor: mix(p.surface, p.accent, 0.1),
            color: p.accent,
          },
          trail: { color: mix(p.text, p.surface, 0.4) },
        },
      };
    },
  },
  methods: {
    variant(index) {
      return index === 0 ? this.variants.primary : this.variants.secondary;
    },
    handleLinkClick(url) {
      window.open(url, "_blank");
    },
    handleGoToDownload() {
      this.$router.push({
        name: "downloadPage",
        params: { ...this.$route.params },
      });
    },
    handleGoToProfile() {
      this.$router.push({
        name: "customerProfilePage",
        params: {
          shortenedSignupPageId: this.$route.params.shortenedSignupPageId,
        },
      });
    },
    handleGoToCreateCustomer() {
      this.$router.push({
        name: "createCustomerPage",
        params: { ...this.$route.params },
        query: {
          ...this.$route.query,
        },
      });
    },
    handleContinueWithoutSignup() {
      window.location = this.$route.query.lastappUrl;
    },
  },
  components: {
    LoginForm,
    CIcon,
  },
};
</script>
